import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Button,
    Checkbox,
    PopoverShell,
    Radio,
    Search,
    SparkIcon,
    TagColored,
    getOverlayPortalParent,
    useEscapeKey,
} from '@spark';
import { DropdownFooter } from '@/features/datasets/components/DataSelection/EditColumnsDropdown';
import { NONE_SELECTED, UNLABELED } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { DatasetOrigin } from '@/shared/domain/taskTypes';

/** Staging Data Editor options for the Data Creation column (antd `filters`). */
export const DATA_CREATION_TYPE_FILTERS = [
    { label: 'Human created', value: DatasetOrigin.human_created },
    { label: 'LLM created', value: DatasetOrigin.commercial_model },
    { label: 'Other', value: DatasetOrigin.other },
    { label: 'AI generated', value: DatasetOrigin.specific_ai_fully_generated },
    { label: 'AI labeled', value: DatasetOrigin.specific_ai_labeled },
    { label: 'HuggingFace', value: DatasetOrigin.huggingface },
    { label: 'SDK', value: DatasetOrigin.sdk },
];

/**
 * Draft (not yet applied) filter selection, seeded from the committed keys.
 * Re-syncs only when the committed keys change by VALUE, never by array
 * identity: parents rebuild columns on every render (e.g. websocket progress
 * updates for training / uploads / AI generation) and pass fresh
 * `currentFilters.x || []` arrays, which must not wipe the in-progress
 * selection while the panel is open.
 */
function useDraftSelection(committedKeys) {
    const [selectedKeys, setSelectedKeys] = useState(() => [...(committedKeys || [])]);
    const committedSignature = JSON.stringify(committedKeys || []);

    useEffect(() => {
        setSelectedKeys(JSON.parse(committedSignature));
    }, [committedSignature]);

    return [selectedKeys, setSelectedKeys];
}

/** Unique Sample Origin names from usecase.datasets / usecase.benchmarks. */
export function uniqueDatasetFilterNames(datasetNames = []) {
    const names = (Array.isArray(datasetNames) ? datasetNames : [])
        .map((item) => {
            if (typeof item === 'string') return item;
            return item?.name || item?.dataset_name || '';
        })
        .map((name) => String(name).trim())
        .filter(Boolean);
    return Array.from(new Set(names));
}

/**
 * Spark icon on every Data Editor column filter trigger: the Figma "Filter
 * Icon" (Spark DS `9008:127`, three lines narrowing downwards), not the
 * legacy funnel still used by toolbar filter buttons.
 */
export const COLUMN_FILTER_ICON_SLUG = 'filter-lines';

/**
 * Column header title + filter-lines trigger. Opens a PopoverShell panel
 * portaled to document.body (or the Spark modal root when opened inside one)
 * so DataTable overflow:hidden cannot clip it. A body portal at z-index 1100
 * would paint behind SparkModalPortal (1300); getOverlayPortalParent matches
 * DropdownMenu so the same Labels / Reviewed panels work in Data Editor and
 * the Improve wizard.
 * `active` is true when that column currently has a filter applied.
 */
export function ColumnFilterHeader({ title, label, active = false, children, onOpenChange }) {
    const [open, setOpen] = useState(false);
    const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
    const wrapRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const filterName = label || (typeof title === 'string' ? title : 'column');
    const filterAriaLabel = active ? `${filterName} filter on` : `Filter ${filterName}`;
    const closePanel = useCallback(() => setOpen(false), []);
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;

    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return undefined;
        const updatePosition = () => {
            const rect = triggerRef.current.getBoundingClientRect();
            setPanelPos({
                top: rect.bottom + 4,
                left: rect.left,
            });
        };
        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (e) => {
            if (wrapRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    useEffect(() => {
        onOpenChangeRef.current?.(open);
        return () => {
            if (open) onOpenChangeRef.current?.(false);
        };
    }, [open]);

    useEscapeKey(closePanel, open);

    return (
        <div ref={wrapRef} className="dataset-column-filter-header">
            <span>{title}</span>
            <span ref={triggerRef}>
                <Button
                    variant={active ? 'secondary' : 'ghost'}
                    size="tiny"
                    type="button"
                    className={[
                        'dataset-column-filter-trigger',
                        active ? 'dataset-column-filter-trigger--active' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    aria-label={filterAriaLabel}
                    aria-expanded={open}
                    aria-pressed={active}
                    iconSlug={COLUMN_FILTER_ICON_SLUG}
                    icon={<SparkIcon slug={COLUMN_FILTER_ICON_SLUG} size="small" />}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen((prev) => !prev);
                    }}
                />
            </span>
            {open
                ? createPortal(
                    <div
                        ref={panelRef}
                        className="dataset-column-filter-panel"
                        style={{
                            position: 'fixed',
                            top: panelPos.top,
                            left: panelPos.left,
                            zIndex: 1100,
                            pointerEvents: 'auto',
                        }}
                    >
                        <PopoverShell tip={false}>
                            {typeof children === 'function'
                                ? children({ close: closePanel })
                                : children}
                        </PopoverShell>
                    </div>,
                    getOverlayPortalParent(triggerRef.current),
                )
                : null}
        </div>
    );
}

/**
 * Labels column filter: search, Unlabeled / None Selected buckets (when count > 0),
 * each label with occurrence count, Clear + OK. Matches staging Data Editor behavior.
 */
export function LabelsFilterPanel({
    selectedKeys: committedKeys = [],
    onApply,
    onClear,
    onClose,
    allLabelOptions,
    isMultilabel = false,
    labelCounts = {},
    restrictToCountedLabels = false,
    unlabeledCount = 0,
    noneSelectedCount = 0,
    searchPlaceholder = 'Search all labels',
    /** Fired once when the panel opens (it mounts per open). Used to refresh
     * the live label / Unlabeled / None Selected counts so the options and
     * numbers are correct at the moment of use, regardless of which mutation
     * (upload, edit, bulk op, AI label generation) changed them last. */
    onPanelOpen,
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedKeys, setSelectedKeys] = useDraftSelection(committedKeys);

    const onPanelOpenRef = useRef(onPanelOpen);
    onPanelOpenRef.current = onPanelOpen;
    useEffect(() => {
        onPanelOpenRef.current?.();
    }, []);

    const labelCountsMap = labelCounts || {};
    const optionLabels = Array.isArray(allLabelOptions?.label_names)
        ? allLabelOptions.label_names
        : [];
    const countedLabels = Object.keys(labelCountsMap);
    const baseLabels = restrictToCountedLabels
        ? countedLabels
        : Array.from(new Set([...optionLabels, ...countedLabels]));

    const filteredLabels = baseLabels
        .filter((label) => typeof label === 'string' && label.trim().length > 0)
        .filter((label) => {
            const total = labelCountsMap?.[label]?.total;
            return typeof total === 'number' ? total > 0 : true;
        })
        .sort((a, b) => a.localeCompare(b))
        .filter((label) => label.toLowerCase().includes(searchTerm.toLowerCase()));

    const showUnlabeled =
        unlabeledCount > 0 &&
        (!searchTerm || UNLABELED.toLowerCase().includes(searchTerm.toLowerCase()));
    const showNoneSelected =
        isMultilabel &&
        noneSelectedCount > 0 &&
        (!searchTerm || NONE_SELECTED.toLowerCase().includes(searchTerm.toLowerCase()));

    const selectExclusive = (key) => setSelectedKeys([key]);

    const toggleMultilabel = (label, checked) => {
        if (checked) {
            const base = selectedKeys.filter(
                (key) => key !== NONE_SELECTED && key !== UNLABELED
            );
            setSelectedKeys([...base, label]);
            return;
        }
        setSelectedKeys(selectedKeys.filter((key) => key !== label));
    };

    return (
        <div className="dataset-labels-filter">
            <Search
                size="small"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm('')}
                showClearButton={!!searchTerm}
            />
            <div className="dataset-labels-filter__list optune-scrollbar">
                {showUnlabeled ? (
                    <div
                        className="dataset-labels-filter__row"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            if (isMultilabel) {
                                if (selectedKeys.includes(UNLABELED)) {
                                    setSelectedKeys(selectedKeys.filter((key) => key !== UNLABELED));
                                } else {
                                    selectExclusive(UNLABELED);
                                }
                            } else {
                                selectExclusive(UNLABELED);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.currentTarget.click();
                            }
                        }}
                    >
                        {isMultilabel ? (
                            <Checkbox
                                label=""
                                status={selectedKeys.includes(UNLABELED) ? 'active' : 'inactive'}
                            />
                        ) : (
                            <Radio
                                label=""
                                status={selectedKeys.includes(UNLABELED) ? 'active' : 'inactive'}
                            />
                        )}
                        <TagColored variant="disabled" label={UNLABELED} />
                        <span className="dataset-labels-filter__count">({unlabeledCount})</span>
                    </div>
                ) : null}
                {showNoneSelected ? (
                    <div
                        className="dataset-labels-filter__row"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                            if (selectedKeys.includes(NONE_SELECTED)) {
                                setSelectedKeys(selectedKeys.filter((key) => key !== NONE_SELECTED));
                            } else {
                                selectExclusive(NONE_SELECTED);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.currentTarget.click();
                            }
                        }}
                    >
                        <Checkbox
                            label=""
                            status={selectedKeys.includes(NONE_SELECTED) ? 'active' : 'inactive'}
                        />
                        <TagColored variant="disabled" label={NONE_SELECTED} />
                        <span className="dataset-labels-filter__count">({noneSelectedCount})</span>
                    </div>
                ) : null}
                {filteredLabels.map((label) => {
                    const total = labelCountsMap?.[label]?.total;
                    const countSuffix = typeof total === 'number' ? `(${total})` : '';
                    const variant = getSparkTagVariant(label, optionLabels) || 'disabled';
                    return (
                        <div
                            key={label}
                            className="dataset-labels-filter__row"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                if (isMultilabel) {
                                    toggleMultilabel(label, !selectedKeys.includes(label));
                                } else {
                                    selectExclusive(label);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                        >
                            {isMultilabel ? (
                                <Checkbox
                                    label=""
                                    status={selectedKeys.includes(label) ? 'active' : 'inactive'}
                                />
                            ) : (
                                <Radio
                                    label=""
                                    status={selectedKeys.includes(label) ? 'active' : 'inactive'}
                                />
                            )}
                            <TagColored variant={variant} label={label} />
                            {countSuffix ? (
                                <span className="dataset-labels-filter__count">{countSuffix}</span>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            <DropdownFooter
                onReset={() => {
                    setSearchTerm('');
                    setSelectedKeys([]);
                    onClear?.();
                }}
                onOk={() => {
                    onApply?.(selectedKeys);
                    onClose?.();
                }}
                hasSelectedItems={selectedKeys.length > 0 || searchTerm.length > 0}
            />
        </div>
    );
}

/** Reviewed column filter: Reviewed / Needs review radios + Clear/OK. */
export function ReviewedFilterPanel({
    selectedKey = null,
    onApply,
    onClear,
    onClose,
}) {
    const [selected, setSelected] = useState(selectedKey);

    useEffect(() => {
        setSelected(selectedKey);
    }, [selectedKey]);

    return (
        <div className="dataset-reviewed-filter">
            <div className="dataset-reviewed-filter__list">
                <Radio
                    label="Reviewed"
                    status={selected === 'reviewed' ? 'active' : 'inactive'}
                    onChange={() => setSelected('reviewed')}
                />
                <Radio
                    label="Needs review"
                    status={selected === 'unreviewed' ? 'active' : 'inactive'}
                    onChange={() => setSelected('unreviewed')}
                />
            </div>
            <DropdownFooter
                onReset={() => {
                    setSelected(null);
                    onClear?.();
                }}
                onOk={() => {
                    onApply?.(selected || null);
                    onClose?.();
                }}
                hasSelectedItems={!!selected}
            />
        </div>
    );
}

/** Origin labels column: show mismatches only / show matches only (scalars to the API). */
export function OriginLabelsFilterPanel({
    selectedKey = null,
    onApply,
    onClear,
    onClose,
}) {
    const [selected, setSelected] = useState(selectedKey);

    useEffect(() => {
        setSelected(selectedKey);
    }, [selectedKey]);

    return (
        <div className="dataset-reviewed-filter">
            <div className="dataset-reviewed-filter__list">
                <Radio
                    label="Show mismatches only"
                    status={selected === 'mismatch' ? 'active' : 'inactive'}
                    onChange={() => setSelected('mismatch')}
                />
                <Radio
                    label="Show matches only"
                    status={selected === 'match' ? 'active' : 'inactive'}
                    onChange={() => setSelected('match')}
                />
            </div>
            <DropdownFooter
                onReset={() => {
                    setSelected(null);
                    onClear?.();
                }}
                onOk={() => {
                    onApply?.(selected || null);
                    onClose?.();
                }}
                hasSelectedItems={!!selected}
            />
        </div>
    );
}

/** Rows visible in the Tools Called filter before its list starts scrolling. */
export const TOOLS_FILTER_VISIBLE_ROWS = 5;

/**
 * Tools Called column filter (agentic tasks): a search box, then one row per
 * distinct tool of the tab — checkbox + `name()` `TagColored` in the same color
 * the TOOLS CALLED column uses for that tool (`variantByName`) — and Clear / OK.
 * Multi-select is OR (the server matches any checked name).
 *
 * The search box sits above the list, outside its scroll area, so it stays put
 * while the list scrolls; the list only clamps its height (to about
 * {@link TOOLS_FILTER_VISIBLE_ROWS} rows) once the tab has more tools than that.
 */
export function ToolsCalledFilterPanel({
    toolNames = [],
    variantByName,
    selectedKeys: committedKeys = [],
    onApply,
    onClear,
    onClose,
    searchPlaceholder = 'Search tools',
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedKeys, setSelectedKeys] = useDraftSelection(committedKeys);

    const needle = searchTerm.trim().toLowerCase();
    const visibleTools = toolNames.filter((name) => !needle || name.toLowerCase().includes(needle));
    const scrolls = toolNames.length > TOOLS_FILTER_VISIBLE_ROWS;

    const toggle = (name) => {
        setSelectedKeys((prev) => (
            prev.includes(name) ? prev.filter((key) => key !== name) : [...prev, name]
        ));
    };

    return (
        <div className="dataset-labels-filter dataset-tools-filter">
            <Search
                size="small"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm('')}
                showClearButton={!!searchTerm}
            />
            <div
                className={[
                    'dataset-labels-filter__list',
                    'dataset-tools-filter__list',
                    scrolls ? 'dataset-tools-filter__list--scroll optune-scrollbar' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
                data-testid="dataset-tools-filter-list"
            >
                {visibleTools.map((name) => (
                    <div
                        key={name}
                        className="dataset-labels-filter__row"
                        role="button"
                        tabIndex={0}
                        onClick={() => toggle(name)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.currentTarget.click();
                            }
                        }}
                    >
                        <Checkbox
                            label=""
                            status={selectedKeys.includes(name) ? 'active' : 'inactive'}
                        />
                        <TagColored variant={variantByName?.get?.(name) || 'disabled'} label={`${name}()`} />
                    </div>
                ))}
                {visibleTools.length === 0 ? (
                    <span className="dataset-tools-filter__empty">No matching tools</span>
                ) : null}
            </div>
            <DropdownFooter
                onReset={() => {
                    setSearchTerm('');
                    setSelectedKeys([]);
                    onClear?.();
                }}
                onOk={() => {
                    onApply?.(selectedKeys);
                    onClose?.();
                }}
                hasSelectedItems={selectedKeys.length > 0 || searchTerm.length > 0}
            />
        </div>
    );
}

/** Multi-select checkbox filter (Sample Origin / Data Creation). */
export function CheckboxFilterPanel({
    options = [],
    selectedKeys: committedKeys = [],
    onApply,
    onClear,
    onClose,
}) {
    const [selectedKeys, setSelectedKeys] = useDraftSelection(committedKeys);

    const toggle = (value) => {
        setSelectedKeys((prev) => (
            prev.includes(value) ? prev.filter((key) => key !== value) : [...prev, value]
        ));
    };

    return (
        <div className="dataset-checkbox-filter">
            <div className="dataset-checkbox-filter__list optune-scrollbar">
                {options.map(({ label, value }) => (
                    <Checkbox
                        key={value}
                        label={label}
                        status={selectedKeys.includes(value) ? 'active' : 'inactive'}
                        onChange={() => toggle(value)}
                    />
                ))}
            </div>
            <DropdownFooter
                onReset={() => {
                    setSelectedKeys([]);
                    onClear?.();
                }}
                onOk={() => {
                    onApply?.(selectedKeys);
                    onClose?.();
                }}
                hasSelectedItems={selectedKeys.length > 0}
            />
        </div>
    );
}
